import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useBankAccountReasonService from '../../app/services/hooks/useBankAccountReasonService';
import { BankAccountReason, BankAccountReasonFilters } from '../../app/models/BankAccountReason';
import BankAccountReasonDataTable from './components/tables/BankAccountReasonDataTable';
import BankAccountReasonCreate from './components/BankAccountReasonCreate';
import BankAccountReasonEdit from './components/BankAccountReasonEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Motivos de cuenta', url: '/bank-account-reasons', isActive: true }
];

const BankAccountReasons = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const {
        fetchingGetBankAccountReasons,
        getBankAccountReasons,
        deleteBankAccountReason,
        activeBankAccountReason
    } = useBankAccountReasonService();
    const [bankAccountReasons, setBankAccountReasons] = useState<BankAccountReason[]>([]);
    const [reasonIdEdit, setReasonIdEdit] = useState<number>(-1);
    const [showingCreate, setShowingCreate] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [filter, setFilter] = useState<BankAccountReasonFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    const reloadTable = () => {
        getBankAccountReasons(filter, {
            onSuccess: (response: ServiceResponse) => {
                setBankAccountReasons(response.data.bank_account_reasons);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data?.to_dashboard) navigationPage('/');
            }
        });
    };

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const showEdit = (id: number) => {
        setShowingEdit(true);
        setReasonIdEdit(id);
    };
    const hideEdit = () => {
        setShowingEdit(false);
        setReasonIdEdit(-1);
    };

    const destroy = (id: number) => {
        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: 'Está a punto de eliminar el motivo de cuenta #' + id,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando motivo de cuenta...');
                        deleteBankAccountReason(id, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El motivo de cuenta no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    const active = (id: number, activeVal: boolean) => {
        activeBankAccountReason(id, activeVal, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Motivos de cuenta" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <DefaultToolBar
                    left={
                        <ButtonCreate
                            callbackCreate={() => setShowingCreate(true)}
                            title="Nuevo motivo de cuenta"
                        />
                    }
                />
                <BankAccountReasonDataTable
                    bankAccountReasons={bankAccountReasons}
                    totalRows={totalRows}
                    loading={fetchingGetBankAccountReasons}
                    edit={showEdit}
                    destroy={destroy}
                    active={active}
                    filter={filter}
                    setFilter={setFilter}
                    callbackSuccess={reloadTable}
                    onSearch={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={() => setShowingCreate(false)}
                    title="Crear motivo de cuenta"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountReasonCreate
                        onSaved={() => {
                            reloadTable();
                            setShowingCreate(false);
                        }}
                        onCancel={() => setShowingCreate(false)}
                    />
                </DefaultModal>
            )}

            {showingEdit && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar motivo de cuenta"
                    backdrop={true}
                    showFooter={false}
                >
                    <BankAccountReasonEdit
                        bankAccountReasonId={reasonIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default BankAccountReasons;
